import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { appendQueryString, hasValidProps, logException, ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import ProgressBar from 'Components/ProgressBar';
import PasswordField from 'Components/PasswordField';
const { EVENTS } = analytics;

const Login = (props) => {
  const [submitting, setSubmitting] = useState(false);
  const [validForm, setValidForm] = useState(false);
  const [password, setPassword] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const { email, strings, transitionTo, uim } = props;

  useEffect(() => {
    if (!hasValidProps(email)) {
      props.history.push(appendQueryString(ROUTES.PAGE_VALIDATE.url));
    }
  }, [email]);

  const isValidField = (value) => {
    const valid = uim.passwordRegExp.test(value);
    const isRequiredMsg = strings.isRequired.formatUimTranslation(
      strings.password
    );
    const message = valid ? '' : isRequiredMsg;
    return { valid, message };
  };

  const handleChange = ({ target }) => {
    const val = target.value;
    const valid = isValidField(val).valid;
    setPassword(val);
    setValidForm(valid);
  };

  const requestPasswordReset = async (event) => {
    analytics.event({ action: EVENTS.forgotPassword, label: 'forgotPassword' });
    setSubmitting(true);
    try {
      await uim.uimApi.passwordReset(email);
    } catch (e) {
      // RBMN-6263 let's swallow error message to expose less info for security
      logException(e);
    }
    transitionTo(ROUTES.PAGE_FORGOT_PASSWORD.url, { email });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    analytics.event({ action: EVENTS.submit, label: 'signin' });
    setSubmitting(true);
    try {
      setFailureMessage('');
      await uim.login(email, password);
    } catch (e) {
      // if application does not support "login-after-registration"
      if (e.code === 'uimapi.account.not.verified') {
        transitionTo(ROUTES.PAGE_VERIFICATION_CODE.url);
      } else if (e.code !== 'uimapi.login.failed') {
        logException(e);
      } else {
        setSubmitting(false);
        setFailureMessage(e.message);
      }
    }
  };

  return (
    <div>
      {submitting && <ProgressBar />}
      <h1 className="page-heading">{email}</h1>
      <Link
        className="sublink"
        to={appendQueryString(ROUTES.PAGE_VALIDATE.url)}
      >
        {strings.notYourAccount}
      </Link>
      <form onSubmit={handleSubmit} className="login-form">
        <PasswordField
          id="pw"
          className="password-input"
          name="pw"
          label={strings.password}
          failureMessage={failureMessage}
          onChange={handleChange}
          isValid={isValidField}
          autoFocus
          showText={strings.show}
          hideText={strings.hide}
        />
        <div className="sublink" onClick={requestPasswordReset}>
          {strings.forgotPassword}
        </div>
        <button type="submit" disabled={!validForm || submitting}>
          {strings.signIn}
        </button>
      </form>
    </div>
  );
};

Login.propTypes = {
  email: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  transitionTo: PropTypes.func.isRequired,
  uim: PropTypes.object.isRequired,
};

export default Login;
